import { ConfigProvider, Select } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs, ManipulateType } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import StyledRangePicker from '@/components/DatePicker/StyledRangePicker';
import { MainText } from '@/components/Typography/NamedTypo';
import useColor from '@/hooks/domain/useColor';
import useStates from '@/hooks/pure/useStates';
import {
  getWorkspaceStorageItem,
  setWorkspaceStorageItem,
} from '@/hooks/pure/useWorkspaceStorage';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import getDatePreset, {
  getDateRangeByDateT,
  RangeUnit,
  rangeUnitMap,
} from '@/utils/getDatePreset';
import {
  DATE_FORMAT,
  getDateRangeWithEndDateOffset,
  getEndDateManipulatedDateRange,
  getTimezoneDayjs,
} from '@/utils/time';

const { Option } = Select;

export enum EntireRangeUnit {
  Daily = 1,
  Weekly,
  Monthly,
  Entire,
}

type Props = {
  dateState: NullableDateFilterState;
  setDateState: (state: NullableDateFilterState) => void;
  reverse?: boolean;
  hideRange?: boolean;
  size?: SizeType;
  allowEntirePeriod?: boolean;
};

export type NullableDateFilterState = {
  dateRange: [Dayjs, Dayjs] | null;
  rangeUnit: EntireRangeUnit;
  presetItem: number | null;
};

const getRangeUnit = (rangeUnit: EntireRangeUnit): RangeUnit => {
  switch (rangeUnit) {
    case EntireRangeUnit.Daily:
      return RangeUnit.Daily;
    case EntireRangeUnit.Weekly:
      return RangeUnit.Weekly;
    case EntireRangeUnit.Monthly:
      return RangeUnit.Monthly;
    case EntireRangeUnit.Entire:
      throw new Error('Need to narrow down before call this function');
  }
};

const EntireDatePresetFilter = (props: Props) => {
  const color = useColor();
  const { rangeUnit, presetItem, dateRange } = props.dateState;
  const onPresetItemClick = (value: number) => {
    if (rangeUnit === EntireRangeUnit.Entire) {
      props.setDateState({
        dateRange: null,
        rangeUnit: rangeUnit,
        presetItem: null,
      });
      return;
    }

    const scopredRangeUnit = getRangeUnit(rangeUnit);
    const range = getDateRangeByDateT(scopredRangeUnit, value);
    props.setDateState({
      rangeUnit,
      dateRange: range,
      presetItem: value,
    });
  };

  const presetOptions = useMemo(() => {
    if (rangeUnit === EntireRangeUnit.Entire) {
      return [];
    }
    const scopredRangeUnit = getRangeUnit(rangeUnit);
    return getDatePreset(scopredRangeUnit, true).map((preset) => {
      return {
        label: <Flex padding="0px 4px">{preset.name}</Flex>,
        value: preset.value,
      };
    });
  }, [rangeUnit]);

  const pickerType = useMemo(() => {
    if (rangeUnit === EntireRangeUnit.Entire) {
      return undefined;
    }
    return rangeUnitMap[rangeUnit] as RangePickerProps['picker'];
  }, [rangeUnit]);

  const onRangeUnitSelect = (value: EntireRangeUnit) => {
    if (value === EntireRangeUnit.Entire) {
      props.setDateState({
        dateRange: null,
        rangeUnit: value,
        presetItem: null,
      });
      return;
    }
    const scopredRangeUnit = getRangeUnit(value);
    let presetValue = value === EntireRangeUnit.Daily ? 2 : 3;
    const dateRange = getDateRangeByDateT(scopredRangeUnit, presetValue);

    props.setDateState({
      dateRange,
      rangeUnit: value,
      presetItem: presetValue,
    });
  };

  const currentValue = getDateRangeWithEndDateOffset(dateRange);

  const onChange = (dates: RangePickerProps['value'] | null) => {
    if (rangeUnit === EntireRangeUnit.Entire) {
      return;
    }
    const opType = rangeUnitMap[rangeUnit] as ManipulateType;
    const newDateRange = getEndDateManipulatedDateRange(
      dates,
      dateRange,
      opType,
    );

    props.setDateState({
      rangeUnit,
      presetItem: null,
      dateRange: newDateRange,
    });
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Select: {
            borderRadius: 24,
            borderRadiusXS: 24,
            borderRadiusSM: 24,
            borderRadiusLG: 24,
          },
          DatePicker: {
            borderRadius: 24,
            borderRadiusXS: 24,
            borderRadiusSM: 24,
            borderRadiusLG: 24,
          },
        },
      }}
    >
      <Style
        a="center"
        g={6}
        w="wrap"
        style={{ rowGap: 12, width: 'fit-content' }}
        d={props.reverse ? 'row-reverse' : 'row'}
      >
        {!props.hideRange && dateRange && (
          <MainText color={color.gray600}>
            {dateRange[0].format(DATE_FORMAT.readable)} -{' '}
            {dateRange[1]
              .subtractVal(1, 'day')
              .format(DATE_FORMAT.readable)}{' '}
          </MainText>
        )}

        <Flex g={6}>
          <Select
            size={props.size || 'small'}
            onSelect={onRangeUnitSelect}
            value={rangeUnit}
            style={{ width: 110 }}
          >
            <Option value={EntireRangeUnit.Daily}>Daily</Option>
            <Option value={EntireRangeUnit.Weekly}>Weekly</Option>
            <Option value={EntireRangeUnit.Monthly}>Monthly</Option>
            {props.allowEntirePeriod && (
              <Option value={EntireRangeUnit.Entire}>Entire</Option>
            )}
          </Select>
          {rangeUnit !== EntireRangeUnit.Entire && (
            <Select
              size={props.size || 'small'}
              onSelect={onPresetItemClick}
              value={presetItem}
              style={{ width: 120 }}
              options={presetOptions}
            />
          )}
        </Flex>
        {rangeUnit !== EntireRangeUnit.Entire && (
          <StyledRangePicker
            allowEmpty
            size={props.size || 'small'}
            picker={pickerType}
            value={currentValue}
            onChange={onChange}
          />
        )}
      </Style>
    </ConfigProvider>
  );
};

const Style = styled(Flex)``;

type DatePresetFilterProps = {
  date_preset_key: string;
  range_unit_key: string;
  date_range_key: string;
};

const useEntireDatePresetFilter = ({
  keyProps,
  initialValue,
}: {
  keyProps?: DatePresetFilterProps;
  initialValue?: NullableDateFilterState;
}) => {
  const wa = useWorkspaceStore((state) => state.currWorkspace?.alias);

  const { state: dateState, setState: setDateState } =
    useStates<NullableDateFilterState>(
      initialValue || {
        dateRange: [
          getTimezoneDayjs().startOf('day').addVal(-30, 'day'),
          getTimezoneDayjs().startOf('day'),
        ],
        presetItem: 3,
        rangeUnit: EntireRangeUnit.Monthly,
      },
    );
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (!wa) return;
    const presetItem = getWorkspaceStorageItem(
      keyProps?.date_preset_key || '',
      dateState.presetItem,
    );
    const unit = getWorkspaceStorageItem(
      keyProps?.range_unit_key || '',
      dateState.rangeUnit,
    );
    if (unit === EntireRangeUnit.Entire) {
      setDateState({
        dateRange: null,
        rangeUnit: unit,
        presetItem: null,
      });
    } else {
      const scopredRangeUnit = getRangeUnit(unit);
      let dateRange: [Dayjs, Dayjs] | null = presetItem
        ? getDateRangeByDateT(scopredRangeUnit, presetItem)
        : null;

      if (presetItem === null) {
        if (dateState.dateRange === null) {
          dateRange = null;
        } else {
          // preset이 null일 떄 기본값 할당
          const range = getWorkspaceStorageItem<[Dayjs, Dayjs]>(
            keyProps?.date_range_key || '',
            dateState.dateRange,
            (values: string) => {
              const [a, b] = JSON.parse(values);
              return [getTimezoneDayjs(a), getTimezoneDayjs(b)];
            },
          );
          dateRange = range;
        }
      }
      setDateState({
        presetItem,
        rangeUnit: unit,
        dateRange,
      });
    }
    setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wa]);

  const onDateFilterChange = useCallback(
    (state: NullableDateFilterState) => {
      setDateState(state);
      if (!keyProps) return;
      if (state.presetItem === null) {
        if (state.dateRange) {
          // preset이 custom일때만 range 저장
          setWorkspaceStorageItem(keyProps.date_range_key, [
            state.dateRange[0].toJSON(),
            state.dateRange[1].toJSON(),
          ]);
        }
      }
      setWorkspaceStorageItem(keyProps.date_preset_key, state.presetItem);
      setWorkspaceStorageItem(keyProps.range_unit_key, state.rangeUnit);
    },
    [keyProps, setDateState],
  );

  return { dateState, setDateState, onDateFilterChange, dateInit: init };
};

export default EntireDatePresetFilter;
export { useEntireDatePresetFilter };
